import { type ShiftAuction } from "@clipboard-health/contract-worker-app-bff";
import { useWorkerPreferences } from "@src/appV2/Agents/api/useWorkerPreferences";
import { useWorkerCustomToBaseQualificationNameMap } from "@src/appV2/Qualifications/useWorkerCustomToBaseQualificationNameMap";
import { useIsShiftBlocksEnabled } from "@src/appV2/redesign/Shift/Block/useIsShiftBlocksEnabled";

import { isPriorityShift } from "../../Shift/Priority/isPriorityShift";
import { useGetMultiLicenseAggregatedBlocks } from "../Blocks/useGetMultiLicenseAggregatedBlocks";
import { filterOpenShifts } from "../Filters/filterOpenShifts";
import { useShiftDiscoveryUserFiltersContext } from "../Filters/useUserFiltersContext";
import { useUrgentShiftsData } from "../UrgentShifts/useUrgentShiftsData";
import { useListViewOpenShiftsData } from "./useListViewOpenShiftsData";
import { useShiftListInjector } from "./useShiftListInjector";

export function useFilteredListViewOpenShiftsData() {
  const isShiftBlocksEnabled = useIsShiftBlocksEnabled();

  const {
    distance,
    // Filtering by timeslot happens at the list rendering level
    // because each date can have a different set of timeslots
    shiftTimeSlots: timeSlotFilters,
    includeWindowedOutShifts,
    favoriteWorkplacesOnly,
    ...localUserFilters
  } = useShiftDiscoveryUserFiltersContext();

  const { filters: shiftBlockFilters } = useWorkerPreferences();

  // pay is passed to useListViewOpenShiftsData to know to include offer data,
  // but the actual filtering happens at the filterOpenShifts function
  const { pay } = localUserFilters;

  const {
    shifts = [],
    workplacesMap,
    rateNegotiationsMap = new Map(),
    offersMap,
    shiftAuctionsMap = new Map<string, ShiftAuction>(),
    ...openShiftsDataResult
  } = useListViewOpenShiftsData({
    distance,
    pay,
    includeWindowedOutShifts,
    favoriteWorkplacesOnly,
  });

  // The open shifts data doesn't include all urgent shifts, so we need to fetch them separately
  const { shifts: urgentShifts } = useUrgentShiftsData();

  const { data: blockShifts } = useGetMultiLicenseAggregatedBlocks(
    {
      maxDistanceMiles: shiftBlockFilters.distance,
    },
    {
      enabled: isShiftBlocksEnabled,
    }
  );

  const priorityShifts = shifts.filter((shift) =>
    isPriorityShift({
      priorityTill: shift.attributes.priorityTill,
      window: shift.attributes.window,
    })
  );

  const customToBaseQualificationNameMap = useWorkerCustomToBaseQualificationNameMap();

  const filteredOpenShifts = filterOpenShifts(
    { shifts, offersMap, workplacesMap },
    localUserFilters,
    customToBaseQualificationNameMap
  );

  // shifts post filtering injection.  I.E. in-app ads, the home health banner (once we migrate that), etc...
  const postInjectionShifts = useShiftListInjector(filteredOpenShifts);

  return {
    data: {
      filteredOpenShifts: postInjectionShifts,
      unfilteredOpenShifts: shifts,
      workplacesMap,
      rateNegotiationsMap,
      shiftAuctionsMap,
      offersMap,
      urgentShiftsCount: urgentShifts.length,
      priorityShiftsCount: priorityShifts.length,
      blockShiftsCount: blockShifts?.data.length ?? 0,
      timeSlotFilters,
    },
    ...openShiftsDataResult,
  };
}
